// Üretim Onay Modal — plan §4.5.1 (#screen-gen-confirm).
// Estimate endpoint'inden sectionCount + estimatedDuration + estimatedCost +
// activeProvider + blockerReason alır; "Vazgeç" / "Üretimi Başlat" butonları.
// Success → POST /projects/:id/generate → parent'a versionId döner, parent progress'e yönlendirir.

import { useEffect, useState } from "react";
import type { ProjectGenerateEstimateResponse, ProjectGenerateStartResponse } from "@mahir/shared";
import { api, ApiError } from "../../api/client";

export interface GenerateConfirmModalProps {
  projectId: string;
  open: boolean;
  onClose: () => void;
  onStarted: (result: ProjectGenerateStartResponse) => void;
  /** "Tümünü yeniden üret" akışı için başlık + açıklama farklı (karar #3). */
  mode?: "start" | "regenerate_all";
  /** regenerate_all modunda current version → modal "v{N+1} üretilecek" gösterir. */
  currentVersion?: number;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return "<1 sn";
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `~${sec.toString()} sn`;
  const min = Math.round(sec / 60);
  return `~${min.toString()} dk`;
}

export function formatCost(usd: number): string {
  if (usd === 0) return "$0 (dummy)";
  if (usd < 0.01) return `~$${usd.toFixed(4)}`;
  return `~$${usd.toFixed(2)}`;
}

const BLOCKER_TEXT: Record<string, string> = {
  missing_required: "Zorunlu alanlar eksik — önce forma dön ve tamamla.",
  active_generation: "Bu projede zaten aktif bir üretim var.",
};

export function GenerateConfirmModal(props: GenerateConfirmModalProps): JSX.Element | null {
  const { projectId, open, onClose, onStarted, mode = "start", currentVersion = 0 } = props;
  const isRegenerate = mode === "regenerate_all";
  const [estimate, setEstimate] = useState<ProjectGenerateEstimateResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let aborted = false;
    setLoading(true);
    setError(null);
    setEstimate(null);
    api<ProjectGenerateEstimateResponse>(`/projects/${projectId}/generate/estimate`)
      .then((e) => {
        if (!aborted) setEstimate(e);
      })
      .catch((err: unknown) => {
        if (!aborted) setError(err instanceof Error ? err.message : "Tahmin alınamadı");
      })
      .finally(() => {
        if (!aborted) setLoading(false);
      });
    return () => {
      aborted = true;
    };
  }, [projectId, open]);

  if (!open) return null;

  const handleStart = async (): Promise<void> => {
    setStarting(true);
    setError(null);
    try {
      const result = await api<ProjectGenerateStartResponse>(`/projects/${projectId}/generate`, {
        method: "POST",
        body: {},
      });
      onStarted(result);
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Üretim başlatılamadı");
      }
    } finally {
      setStarting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{ background: "rgba(0,0,0,0.4)" }}
      onClick={onClose}
    >
      <div
        className="rounded-xl p-6 max-w-md w-full mx-4"
        style={{ background: "var(--color-surface)", color: "var(--color-text)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          className="mb-1"
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "1.25rem",
          }}
        >
          {isRegenerate ? "Raporu Yeniden Üret" : "AI Üretimini Başlat"}
        </h2>
        {isRegenerate && (
          <p className="mb-3 text-sm" style={{ color: "var(--color-text-2)" }}>
            v{currentVersion.toString()} korunur; tüm bölümler v{(currentVersion + 1).toString()}{" "}
            olarak yeniden üretilir.
          </p>
        )}
        {!isRegenerate && <div className="mb-3" />}

        {loading && <p style={{ color: "var(--color-text-2)" }}>Tahmin hesaplanıyor…</p>}

        {!loading && estimate && (
          <div className="flex flex-col gap-3 mb-5">
            <div className="flex justify-between text-sm">
              <span style={{ color: "var(--color-text-2)" }}>Bölüm sayısı</span>
              <span style={{ fontWeight: 600 }}>{estimate.sectionCount.toString()}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span style={{ color: "var(--color-text-2)" }}>Tahmini süre</span>
              <span style={{ fontWeight: 600 }}>
                {formatDuration(estimate.estimatedDurationMs)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span style={{ color: "var(--color-text-2)" }}>Tahmini maliyet</span>
              <span style={{ fontWeight: 600 }}>{formatCost(estimate.estimatedCostUsd)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span style={{ color: "var(--color-text-2)" }}>Model</span>
              <span style={{ fontWeight: 600 }}>
                {estimate.activeProvider} · {estimate.activeModel}
              </span>
            </div>
            {!estimate.canGenerate && estimate.blockerReason && (
              <div
                className="rounded-md p-3 mt-1 text-sm"
                style={{
                  background: "color-mix(in srgb, var(--color-error) 10%, white)",
                  color: "var(--color-error)",
                  border: "1px solid color-mix(in srgb, var(--color-error) 25%, white)",
                }}
              >
                {BLOCKER_TEXT[estimate.blockerReason] ?? estimate.blockerReason}
              </div>
            )}
          </div>
        )}

        {error && (
          <div
            className="rounded-md p-3 mb-4 text-sm"
            style={{
              background: "color-mix(in srgb, var(--color-error) 10%, white)",
              color: "var(--color-error)",
            }}
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={starting}
            className="px-4 py-2 rounded-lg text-sm"
            style={{
              background: "transparent",
              border: "1px solid var(--color-border)",
              color: "var(--color-text-2)",
            }}
          >
            Vazgeç
          </button>
          <button
            onClick={() => void handleStart()}
            disabled={starting || loading || !(estimate?.canGenerate ?? false)}
            className="px-4 py-2 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: "var(--color-primary)",
              color: "var(--color-surface)",
              fontWeight: 600,
            }}
          >
            {starting ? "Başlatılıyor…" : isRegenerate ? "Yeniden Üret" : "Üretimi Başlat"}
          </button>
        </div>
      </div>
    </div>
  );
}
